import { rem } from 'polished'
import { useCallback } from 'react'
import styled from 'styled-components'

import BaseBookmarkButton from '../../Common/BaseBookmarkButton'

import { trackClientEvent } from 'analytics/analytics'
import { AnalyticsPage } from 'contexts/Analytics/analyticsPageContext'
import { useAppDispatch } from 'hooks/reduxHooks'
import noop from 'lib/function/noop'
import { clearRecentlySavedTripId } from 'storage/recentSavedTrip'
import { getGoogleExploreItemsPlaceDetails } from 'tripPlanner/api/places'
import {
  ACCOMMODATION_TYPE,
  determineItemType,
} from 'tripPlanner/components/TripMaps/MapSearch'
import { useTripId } from 'tripPlanner/contexts/TripContext'
import { useDeleteTripItem, useCreateTripItem } from 'tripPlanner/hooks/api/tripItem'
import { useTrip } from 'tripPlanner/hooks/api/trip'
import useBookmarkSnackbarHandlers from 'tripPlanner/hooks/bookmarks/useSavedItemSnackbarHandlers'
import { useTripPermissions } from 'tripPlanner/hooks/useTripPermissions'
import { setCurrentSelectionId } from 'tripPlanner/reducers/actions'
import type { FullTrip } from 'tripPlanner/types/common'
import { getMatchingSavedItem } from 'tripPlanner/utils/explore'
import { createExploreItemBookmarkPayload } from 'tripPlanner/utils/itemConversions/bookmarks/exploreItem'
import { tripItemSelectionId } from 'tripPlanner/utils/itemSelection'

const StyledBaseBookmarkButton = styled(BaseBookmarkButton)`
  min-width: ${rem(28)};
`

export interface Props {
  exploreItemId: string
  tripId?: string
  onSnackbarViewClick?: () => void
  onSave?: () => void
  onDelete?: () => void
}

function SearchBookmarkButtonFromPlaceId({
  exploreItemId,
  tripId: tripIdProp,
  onSnackbarViewClick = noop,
  onSave = noop,
  onDelete = noop,
}: Props) {
  const contextTripId = useTripId()
  const tripId = tripIdProp ?? contextTripId
  const { data: trip } = useTrip({ tripId })
  const { canEdit } = useTripPermissions(tripId)

  const savedItem = trip ? getMatchingSavedItem(trip, exploreItemId) : undefined
  const isSaved = !!savedItem

  const {
    showSaveSuccessSnackbar,
    showSaveErrorSnackbar,
    showRemoveSuccessSnackbar,
    showRemoveErrorSnackbar,
  } = useBookmarkSnackbarHandlers()

  const { mutate: deleteBookmark, isLoading: isDeleting } = useDeleteTripItem({
    onSuccess: (_res) => {
      onDelete()
      showRemoveSuccessSnackbar(trip!.id, trip!.name, onSnackbarViewClick)
    },
    onError: (_res, _vars) => {
      showRemoveErrorSnackbar(trip!.name)
    },
  })

  const dispatch = useAppDispatch()

  const { mutateAsync: createItem, isLoading: isCreating } = useCreateTripItem({
    onSuccess: (item) => {
      onSave()
      dispatch(setCurrentSelectionId(tripItemSelectionId(item.id)))
    },
    onError: () => {
      showSaveErrorSnackbar(trip!.name)

      // Clear the recently saved trip ID in case it errored because the trip was deleted
      clearRecentlySavedTripId()
    },
  })
  const createTripItem = useCallback(
    async(trip: FullTrip) => {
      try {
        const data = await getGoogleExploreItemsPlaceDetails([exploreItemId])
        const exploreItem = data.items[0]

        const type =
          determineItemType(exploreItem.types) === ACCOMMODATION_TYPE ?
            'ACCOMMODATION' :
            'ATTRACTION'

        const payload = createExploreItemBookmarkPayload({
          exploreItem,
          type,
        })
        const createdItem = await createItem({
          tripId,
          tripItem: payload,
        })
        showSaveSuccessSnackbar(
          trip.id,
          trip.name,
          createdItem.id,
          undefined,
          undefined,
          onSnackbarViewClick,
        )

        trackClientEvent({
          action: 'result',
          subject: 'save_map_search_result',
          category: AnalyticsPage.tripPlanner,
          type: 'interaction',
        })
      } catch {
        // Do nothing - failure case is handled by the hook's onError handler
      }
    },
    [
      exploreItemId,
      createItem,
      tripId,
      showSaveSuccessSnackbar,
      onSnackbarViewClick,
    ],
  )

  const onButtonClick = useCallback(async() => {
    if (!trip) {
      return
    }
    if (savedItem) {
      deleteBookmark({
        tripId: trip.id,
        tripItemId: savedItem.id,
      })
    } else {
      createTripItem(trip)
    }
  }, [trip, savedItem, deleteBookmark, createTripItem])

  if (!canEdit || !tripId) {
    return null
  }

  return (
    <StyledBaseBookmarkButton
      isSaved={isSaved}
      onClick={onButtonClick}
      isProcessing={isDeleting || isCreating}
    />
  )
}

export default SearchBookmarkButtonFromPlaceId
